import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Plus, Upload, ArrowLeft, Sparkles, Download, Trash2, Edit } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import EditQuestionModal from '../../components/EditQuestionModal';
import LaTeXRenderer from '../../components/LaTeXRenderer';
import BulkUploadProgress from '../../components/BulkUploadProgress';

function QuestionBankDetail() {
  const { bankId } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isFaculty = user.role === 'faculty';
  const [bank, setBank] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editQuestion, setEditQuestion] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  useEffect(() => {
    fetchBankDetails();
  }, [bankId]);

  const fetchBankDetails = async () => {
    try {
      const [bankRes, questionsRes] = await Promise.all([
        api.get(`/question-banks/${bankId}`),
        api.get(`/questions/bank/${bankId}`),
      ]);
      setBank(bankRes.data);
      setQuestions(questionsRes.data);
    } catch (error) {
      console.error('Error fetching bank details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('bank_id', bankId);

    try {
      const response = await api.post('/questions/bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(response.data.message);
      if (response.data.errors && response.data.errors.length > 0) {
        console.error('Upload errors:', response.data.errors);
      }
      setUploadOpen(false);
      fetchBankDetails();
    } catch (error) {
      console.error('Error uploading questions:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    
    try {
      await api.delete(`/questions/${questionId}`);
      toast.success('Question deleted');
      fetchBankDetails();
    } catch (error) {
      console.error('Error deleting question:', error);
      toast.error('Failed to delete question');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/question-banks')}
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Question Banks
        </Button>

        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="bank-title">{bank?.title}</h1>
              <p className="text-gray-600">{bank?.subject} • {questions.length} questions</p>
            </div>
            <div className="flex gap-3">
              {!isFaculty && (
                <Button
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={() => navigate('/admin/questions/generate-ai', { state: { bankId } })}
                  data-testid="ai-generate-button"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  AI Generate
                </Button>
              )}
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const response = await api.get('/questions/sample-template');
                    const blob = new Blob([response.data.content], { type: 'text/csv' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = response.data.filename;
                    a.click();
                    toast.success('Template downloaded');
                  } catch (error) {
                    toast.error('Failed to download template');
                  }
                }}
                data-testid="download-template-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
              {!isFaculty && (
              <>
              <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
                <Button
                  variant="outline"
                  onClick={() => setUploadOpen(true)}
                  data-testid="bulk-upload-button"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Bulk Upload
                </Button>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Bulk Upload Questions</DialogTitle>
                  </DialogHeader>
                  <BulkUploadProgress
                    endpoint="/questions/bulk-upload-stream"
                    extraFields={{ bank_id: bankId }}
                    title="Bulk Upload Questions"
                    description="Upload a CSV or Excel file with questions (max 2000 per upload). Use the template format: Question Type, Question, Opt1-Opt5, CorrectAnsInd, Points, Image URL"
                    onComplete={() => { fetchBankDetails(); toast.success('Questions uploaded successfully'); }}
                    onClose={() => setUploadOpen(false)}
                  />
                </DialogContent>
              </Dialog>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => navigate('/admin/questions/create', { state: { bankId } })}
                data-testid="add-question-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Question
              </Button>
              </>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {questions.map((q, index) => (
            <Card key={q.id} className="p-6 relative" data-testid={`question-card-${index}`}>
              <div className="absolute top-4 right-4 flex gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                  onClick={() => {
                    setEditQuestion(q);
                    setEditModalOpen(true);
                  }}
                  data-testid={`edit-question-${index}`}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                {!isFaculty && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDeleteQuestion(q.id)}
                    data-testid={`delete-question-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <div className="flex items-start justify-between pr-24">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium text-gray-500">Q{index + 1}</span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                      {q.question_type}
                    </span>
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                      {q.difficulty}
                    </span>
                  </div>
                  <div className="text-gray-900 font-medium mb-3">
                    <LaTeXRenderer text={q.question_text} />
                  </div>
                  {q.choices && q.choices.length > 0 && (
                    <div className="space-y-2">
                      {q.choices.map((choice, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <span className="text-sm text-gray-600 mt-1">•</span>
                          <div className="text-sm text-gray-600 flex-1">
                            <LaTeXRenderer text={choice} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {q.explanation && (
                    <div className="mt-3 text-sm text-gray-600">
                      <strong>Explanation:</strong>{' '}
                      <LaTeXRenderer text={q.explanation} />
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">{q.points} points</p>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {questions.length === 0 && (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 mb-2">No questions yet</h3>
            <p className="text-gray-500 mb-4">Add questions to this bank to get started</p>
          </div>
        )}
      </div>

      <EditQuestionModal
        question={editQuestion}
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditQuestion(null);
        }}
        onSuccess={fetchBankDetails}
      />
    </Layout>
  );
}

export default QuestionBankDetail;